import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'

interface GardenBed {
  id: string
  x: number
  y: number
  width: number
  height: number
  plantId: string | null
  plantDate: string | null
  rotation?: number // 0, 90, 180, 270 degrees
}

interface Garden {
  id: string
  name: string
  width: number
  height: number
  beds: GardenBed[]
  createdAt: string
  updatedAt: string
}

interface GardenContextType {
  gardens: Garden[]
  selectedGardenId: string | null
  selectedGarden: Garden | null
  setSelectedGardenId: (id: string | null) => void
  createGarden: (name: string, width?: number, height?: number) => string
  updateGarden: (id: string, updates: Partial<Garden>) => void
  deleteGarden: (id: string) => void
  addBed: (gardenId: string, bed: Omit<GardenBed, 'id'>) => string
  updateBed: (gardenId: string, bedId: string, updates: Partial<GardenBed>) => void
  removeBed: (gardenId: string, bedId: string) => void
  plantInBed: (gardenId: string, bedId: string, plantId: string) => void
  clearBed: (gardenId: string, bedId: string) => void
  getGardenStats: (id: string) => { totalBeds: number; plantedBeds: number; emptyBeds: number; uniquePlants: number; utilization: number } | null
  // My Plants - shared between Garden Designer and Calendar
  myPlants: string[]
  addToMyPlants: (plantId: string) => void
  removeFromMyPlants: (plantId: string) => void
  isInMyPlants: (plantId: string) => boolean
  // Location data - shared across pages
  zipCode: string
  setZipCode: (zip: string) => void
  zone: string
  setZone: (zone: string) => void
  lastFrost: string | null
  setLastFrost: (date: string | null) => void
  firstFrost: string | null
  setFirstFrost: (date: string | null) => void
  dataSource: string
  setDataSource: (source: string) => void
  confidence: string
  setConfidence: (confidence: string) => void
}

const GardenContext = createContext<GardenContextType | null>(null)

export const useGardenContext = () => {
  const context = useContext(GardenContext)
  if (!context) {
    throw new Error('useGardenContext must be used within GardenProvider')
  }
  return context
}

export const GardenProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Load from localStorage on init
  const [gardens, setGardens] = useState<Garden[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('garden_planner_gardens')
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch (e) {
          console.error('Failed to parse gardens:', e)
        }
      }
    }
    return []
  })
  
  const [selectedGardenId, setSelectedGardenId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('garden_planner_selected_id')
    }
    return null
  })

  // My Plants - shared list between Garden Designer and Calendar
  const [myPlants, setMyPlants] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('garden_planner_my_plants')
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch (e) {
          console.error('Failed to parse my plants:', e)
        }
      }
    }
    return []
  })

  // Location data - shared across pages
  const [zipCode, setZipCode] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('garden_planner_zip_code') || ''
    }
    return ''
  })

  const [zone, setZone] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('garden_planner_zone') || ''
    }
    return ''
  })

  const [lastFrost, setLastFrost] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('garden_planner_last_frost')
    }
    return null
  })

  const [firstFrost, setFirstFrost] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('garden_planner_first_frost')
    }
    return null
  })

  const [dataSource, setDataSource] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('garden_planner_data_source') || ''
    }
    return ''
  })

  const [confidence, setConfidence] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('garden_planner_confidence') || ''
    }
    return ''
  })

  // Save myPlants to localStorage
  useEffect(() => {
    localStorage.setItem('garden_planner_my_plants', JSON.stringify(myPlants))
  }, [myPlants])

  // Save location data to localStorage
  useEffect(() => {
    localStorage.setItem('garden_planner_zip_code', zipCode)
  }, [zipCode])

  useEffect(() => {
    localStorage.setItem('garden_planner_zone', zone)
  }, [zone])

  useEffect(() => {
    if (lastFrost) {
      localStorage.setItem('garden_planner_last_frost', lastFrost)
    } else {
      localStorage.removeItem('garden_planner_last_frost')
    }
  }, [lastFrost])

  useEffect(() => {
    if (firstFrost) {
      localStorage.setItem('garden_planner_first_frost', firstFrost)
    } else {
      localStorage.removeItem('garden_planner_first_frost')
    }
  }, [firstFrost])

  useEffect(() => {
    localStorage.setItem('garden_planner_data_source', dataSource)
  }, [dataSource])

  useEffect(() => {
    localStorage.setItem('garden_planner_confidence', confidence)
  }, [confidence])

  const addToMyPlants = useCallback((plantId: string) => {
    setMyPlants(prev => {
      if (prev.includes(plantId)) return prev
      return [...prev, plantId]
    })
  }, [])

  const removeFromMyPlants = useCallback((plantId: string) => {
    setMyPlants(prev => prev.filter(id => id !== plantId))
  }, [])

  const isInMyPlants = useCallback((plantId: string) => {
    return myPlants.includes(plantId)
  }, [myPlants])

  // Save to localStorage whenever gardens change
  useEffect(() => {
    localStorage.setItem('garden_planner_gardens', JSON.stringify(gardens))
  }, [gardens])

  // Save selected ID
  useEffect(() => {
    if (selectedGardenId) {
      localStorage.setItem('garden_planner_selected_id', selectedGardenId)
    } else {
      localStorage.removeItem('garden_planner_selected_id')
    }
  }, [selectedGardenId])

  // Get selected garden
  const selectedGarden = selectedGardenId 
    ? gardens.find(g => g.id === selectedGardenId) || null
    : null

  // Create garden
  const createGarden = useCallback((name: string, width: number = 20, height: number = 20): string => {
    const newGarden: Garden = {
      id: uuidv4(),
      name,
      width,
      height,
      beds: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    
    setGardens(prev => [...prev, newGarden])
    return newGarden.id
  }, [])

  // Update garden
  const updateGarden = useCallback((id: string, updates: Partial<Garden>) => {
    setGardens(prev => prev.map(g => 
      g.id === id 
        ? { ...g, ...updates, updatedAt: new Date().toISOString() }
        : g
    ))
  }, [])

  // Delete garden
  const deleteGarden = useCallback((id: string) => {
    setGardens(prev => prev.filter(g => g.id !== id))
    if (selectedGardenId === id) {
      setSelectedGardenId(null)
    }
  }, [selectedGardenId])

  // Add bed
  const addBed = useCallback((gardenId: string, bed: Omit<GardenBed, 'id'>): string => {
    const newBed: GardenBed = {
      ...bed,
      id: uuidv4()
    }
    
    setGardens(prev => prev.map(g => 
      g.id === gardenId 
        ? { 
            ...g, 
            beds: [...g.beds, newBed],
            updatedAt: new Date().toISOString()
          }
        : g
    ))
    
    return newBed.id
  }, [])

  // Update bed
  const updateBed = useCallback((gardenId: string, bedId: string, updates: Partial<GardenBed>) => {
    setGardens(prev => prev.map(g => 
      g.id === gardenId 
        ? { 
            ...g, 
            beds: g.beds.map(b => 
              b.id === bedId ? { ...b, ...updates } : b
            ),
            updatedAt: new Date().toISOString()
          }
        : g
    ))
  }, [])

  // Remove bed
  const removeBed = useCallback((gardenId: string, bedId: string) => {
    setGardens(prev => prev.map(g => 
      g.id === gardenId 
        ? { 
            ...g, 
            beds: g.beds.filter(b => b.id !== bedId),
            updatedAt: new Date().toISOString()
          }
        : g
    ))
  }, [])

  // Plant in bed
  const plantInBed = useCallback((gardenId: string, bedId: string, plantId: string) => {
    updateBed(gardenId, bedId, {
      plantId,
      plantDate: new Date().toISOString()
    })
  }, [updateBed])

  // Clear bed
  const clearBed = useCallback((gardenId: string, bedId: string) => {
    updateBed(gardenId, bedId, {
      plantId: null,
      plantDate: null
    })
  }, [updateBed])

  // Get stats
  const getGardenStats = useCallback((id: string) => {
    const g = gardens.find(garden => garden.id === id)
    if (!g) return null

    const plantedBeds = g.beds.filter(b => b.plantId)
    const uniquePlants = [...new Set(plantedBeds.map(b => b.plantId).filter(Boolean))]
    
    return {
      totalBeds: g.beds.length,
      plantedBeds: plantedBeds.length,
      emptyBeds: g.beds.length - plantedBeds.length,
      uniquePlants: uniquePlants.length,
      utilization: g.beds.length > 0 ? (plantedBeds.length / g.beds.length) * 100 : 0
    }
  }, [gardens])

  return (
    <GardenContext.Provider value={{
      gardens,
      selectedGardenId,
      selectedGarden,
      setSelectedGardenId,
      createGarden,
      updateGarden,
      deleteGarden,
      addBed,
      updateBed,
      removeBed,
      plantInBed,
      clearBed,
      getGardenStats,
      myPlants,
      addToMyPlants,
      removeFromMyPlants,
      isInMyPlants,
      zipCode,
      setZipCode,
      zone,
      setZone,
      lastFrost,
      setLastFrost,
      firstFrost,
      setFirstFrost,
      dataSource,
      setDataSource,
      confidence,
      setConfidence
    }}>
      {children}
    </GardenContext.Provider>
  )
}
